import { useEffect, useState } from 'react';
import DetailsCard from '../components/DetailsCard/DetailsCard';
import { Box, Divider, Grid, Typography } from '@mui/material';
import BackButton from '../components/Buttons/BackButton';
import Theme from '../Themes/main.theme';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FilmOffice_status,
  FilmOffice_status_Dictionary,
} from '../components/dictionaries/FilmOffice_Status';
import { VerticalStep, VerticalStepper } from '../components/Stepper';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import MovieIcon from '@mui/icons-material/Movie';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DialogAction from '../components/Dialog/DialogAction';
import InfoBoxes from '../components/InfoBoxes/InfoBoxes';
import { useSnackbar } from 'notistack';
import HttpService from '../services/HttpService';
import { useTranslation } from 'react-i18next';
import CardStepContainer from '../components/CardStepsContainer/CardStepContainer';
import ActionButton from '../components/Buttons/ActionButton';

interface Idata {
  id: number;
  title: string;
  email: string;
  phone: string;
  is_enabled: boolean;
  can_be_deleted: boolean;
  active_users: number;
}

const FrontOfficeDetails = () => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<Idata>({
    id: 0,
    title: '',
    email: '',
    phone: '',
    is_enabled: false,
    can_be_deleted: true,
    active_users: 0,
  });

  const navigate = useNavigate();
  const { t } = useTranslation();
  const contact_info_details = [
    {
      name: 'Email',
      value: data?.email,
    },
    {
      name: t('Τηλέφωνο'),
      value: data?.phone,
    },
  ];
  const fo_details = [
    {
      name: t('Κατάσταση'),
      value: data?.is_enabled
        ? FilmOffice_status_Dictionary[FilmOffice_status.Enabled]
        : FilmOffice_status_Dictionary[FilmOffice_status.Disabled],
    },
  ];

  const steps = [
    {
      label: t('Στοιχεία Επικοινωνίας'),
      content: (
        <CardStepContainer
          isLoading={isLoading}
          detailsProps={{ rows: contact_info_details, removeLines: true }}
        />
      ),
      icon: <LocalPhoneIcon />,
    },
    {
      label: t('Χαρακτηριστικά Οργανικής Μονάδας'),
      content: (
        <CardStepContainer
          isLoading={isLoading}
          detailsProps={{ rows: fo_details, removeLines: true }}
        />
      ),
      icon: <MovieIcon />,
    },
  ];

  const getData = async () => {
    try {
      const response = await HttpService.getAxiosClient().get(
        `${process.env.REACT_APP_BACKEND_URL}/registrations/front_office/${id}`
      );
      setData(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const infoDetails = [
    {
      label: t('Ενεργοί Χρήστες'),
      count: data.active_users,
    },
  ];

  const handleSubmit = async () => {
    const response = await HttpService.getAxiosClient().delete(
      `${process.env.REACT_APP_BACKEND_URL}/registrations/front_office/${data.id}`
    );
    response.status === 200 &&
      enqueueSnackbar(
        `Η Διεύθυνσης ΕΚΟΜΕ ${response.data.title} διαγράφτηκε επιτυχώς`,
        { variant: 'success' }
      );
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <>
      <Box mt={2} mb={2}>
        <BackButton onClick={() => navigate('/admin/units/dashboard')} />
      </Box>
      <Typography variant='h2' color={Theme?.labelColor?.main}>
        {t('Προβολή Καρτέλας Διεύθυνσης ΕΚΟΜΕ')}
      </Typography>
      <Divider sx={{ my: 2, border: 1, color: Theme?.dividerColor?.main }} />
      <Grid container spacing={{ xs: 2, lg: 0 }}>
        <Grid item xs={12}
          lg={6}>
          <DetailsCard
            actionButtons={
              <>
                <ActionButton
                  title={t('Επεξεργασία Διεύθυνσης ΕΚΟΜΕ')}
                  icon={<EditIcon />}
                  onClick={() => navigate(`/admin/units/ekome/edit/${id}`)}
                />
                <ActionButton
                  title={t('Διαγραφή Διεύθυνσης ΕΚΟΜΕ')}
                  icon={<DeleteIcon />}
                  disabled={data.is_enabled}
                  onClick={() => setOpen(true)}
                />{' '}
                {/* itan can_be_deleted */}
              </>
            }
            icon={<VideoCameraFrontIcon fontSize='large' />}
            title={data.title}
            isLoading={isLoading}
          >
            <VerticalStepper activeStep={0}>
              {steps.map((step) => {
                return (
                  <VerticalStep
                    active={true}
                    key={step.label}
                    label={step.label}
                    icon={step.icon}
                  >
                    {step.content}
                  </VerticalStep>
                );
              })}
            </VerticalStepper>
          </DetailsCard>
        </Grid>
        <Grid item xs={12}
          lg={6}>
          <Box display='flex' flexDirection='column'
            alignItems='center' ml={2}>
            <InfoBoxes
              infoDetails={infoDetails}
              title={t('​Συγκεντρωτικά Στοιχεία Διεύθυνσης EKOME')}
              direction='column'
              isLoading={isLoading}
            />
          </Box>
        </Grid>
      </Grid>
      <DialogAction
        open={open}
        mode='delete'
        handleClose={() => setOpen(false)}
        content={'Είστε σίγουρος ότι θέλετε να διαγράψετε την Διεύθυνση ΕΚΟΜΕ;'}
        onAccept={() => {
          handleSubmit();
          setOpen(false);
        }}
      />
    </>
  );
};

export default FrontOfficeDetails;
